import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Alert } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types/navigation';
import { Post } from '../types/post';
import { PostCard } from '../components/post';
import { LoadingIndicator, ActionSheet, ActionSheetOption } from '../components/common';
import * as blueskyService from '../services/bluesky';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

type Props = NativeStackScreenProps<RootStackParamList, 'PostDetail'>;

export const PostDetailScreen: React.FC<Props> = ({ route, navigation }) => {
  const { uri } = route.params;
  const [post, setPost] = useState<Post | null>(null);
  const [replies, setReplies] = useState<Post[]>([]);
  const [parent, setParent] = useState<Post | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [repostMenuVisible, setRepostMenuVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);

  useEffect(() => {
    loadPostThread();
  }, [uri]);

  const loadPostThread = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const thread = await blueskyService.getPostThread(uri);

      // メイン投稿
      if (thread.post) {
        setPost(mapThreadPost(thread.post));
      }

      // 親投稿（リプライ元）
      if (thread.parent?.post) {
        setParent(mapThreadPost(thread.parent.post));
      }

      // リプライ
      if (thread.replies && thread.replies.length > 0) {
        const mappedReplies = thread.replies
          .filter((reply: any) => reply.post)
          .map((reply: any) => mapThreadPost(reply.post));
        setReplies(mappedReplies);
      }
    } catch (err) {
      console.error('Load post thread error:', err);
      setError('投稿の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const mapThreadPost = (threadPost: any): Post => {
    return {
      uri: threadPost.uri,
      cid: threadPost.cid,
      author: {
        did: threadPost.author.did,
        handle: threadPost.author.handle,
        displayName: threadPost.author.displayName,
        avatar: threadPost.author.avatar,
      },
      record: {
        text: threadPost.record.text,
        createdAt: threadPost.record.createdAt,
        reply: threadPost.record.reply,
      },
      replyCount: threadPost.replyCount || 0,
      repostCount: threadPost.repostCount || 0,
      likeCount: threadPost.likeCount || 0,
      indexedAt: threadPost.indexedAt,
      viewer: threadPost.viewer,
      embed: threadPost.embed,
    };
  };

  const handleLike = async (targetPost: Post) => {
    try {
      if (targetPost.viewer?.like) {
        await blueskyService.unlikePost(targetPost.viewer.like);
        updatePostLike(targetPost.uri, undefined);
      } else {
        const likeUri = await blueskyService.likePost(targetPost.uri, targetPost.cid);
        updatePostLike(targetPost.uri, likeUri);
      }
    } catch (err) {
      console.error('Like error:', err);
      Alert.alert('エラー', 'いいねに失敗しました');
    }
  };

  const handleRepost = (targetPost: Post) => {
    setSelectedPost(targetPost);
    setRepostMenuVisible(true);
  };

  const executeRepost = async (targetPost: Post) => {
    try {
      if (targetPost.viewer?.repost) {
        await blueskyService.unrepost(targetPost.viewer.repost);
        updatePostRepost(targetPost.uri, undefined);
      } else {
        const repostUri = await blueskyService.repost(targetPost.uri, targetPost.cid);
        updatePostRepost(targetPost.uri, repostUri);
      }
    } catch (err) {
      console.error('Repost error:', err);
      Alert.alert('エラー', 'リポストに失敗しました');
    }
  };

  const handleQuotePost = (targetPost: Post) => {
    navigation.navigate('Compose', {
      quoteTo: {
        uri: targetPost.uri,
        cid: targetPost.cid,
      },
    });
  };

  const repostMenuOptions: ActionSheetOption[] = selectedPost
    ? [
        {
          label: selectedPost.viewer?.repost ? 'リポストを取り消す' : 'リポスト',
          icon: 'repeat-outline',
          onPress: () => {
            if (selectedPost) {
              executeRepost(selectedPost);
            }
          },
        },
        {
          label: '引用',
          icon: 'chatbox-outline',
          onPress: () => {
            if (selectedPost) {
              handleQuotePost(selectedPost);
            }
          },
        },
      ]
    : [];

  const updatePostLike = (targetUri: string, likeUri?: string) => {
    const isLiked = !!likeUri;
    const updatePost = (p: Post | null) => {
      if (!p || p.uri !== targetUri) return p;
      return {
        ...p,
        likeCount: isLiked ? p.likeCount + 1 : p.likeCount - 1,
        viewer: {
          ...p.viewer,
          like: likeUri,
        },
      };
    };

    setPost(updatePost);
    setParent(updatePost);
    setReplies(prev => prev.map(r => (r.uri === targetUri ? updatePost(r)! : r)));
  };

  const updatePostRepost = (targetUri: string, repostUri?: string) => {
    const isReposted = !!repostUri;
    const updatePost = (p: Post | null) => {
      if (!p || p.uri !== targetUri) return p;
      return {
        ...p,
        repostCount: isReposted ? p.repostCount + 1 : p.repostCount - 1,
        viewer: {
          ...p.viewer,
          repost: repostUri,
        },
      };
    };

    setPost(updatePost);
    setParent(updatePost);
    setReplies(prev => prev.map(r => (r.uri === targetUri ? updatePost(r)! : r)));
  };

  if (isLoading) {
    return <LoadingIndicator fullScreen />;
  }

  if (error || !post) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error || '投稿が見つかりません'}</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScrollView>
        {/* 親投稿（リプライ元） */}
        {parent && (
          <View style={styles.parentContainer}>
            <PostCard
              post={parent}
              onLike={() => handleLike(parent)}
              onRepost={() => handleRepost(parent)}
              onAuthorPress={() => navigation.navigate('Profile', { did: parent.author.did })}
            />
            <View style={styles.threadLine} />
          </View>
        )}

        {/* メイン投稿 */}
        <View style={styles.mainPostContainer}>
          <PostCard
            post={post}
            onLike={() => handleLike(post)}
            onRepost={() => handleRepost(post)}
            onAuthorPress={() => navigation.navigate('Profile', { did: post.author.did })}
          />
        </View>

        {/* リプライ一覧 */}
        {replies.length > 0 && (
          <View style={styles.repliesContainer}>
            <Text style={styles.repliesHeader}>返信 ({replies.length})</Text>
            {replies.map(reply => (
              <PostCard
                key={reply.uri}
                post={reply}
                onLike={() => handleLike(reply)}
                onRepost={() => handleRepost(reply)}
                onAuthorPress={() => navigation.navigate('Profile', { did: reply.author.did })}
              />
            ))}
          </View>
        )}

        {replies.length === 0 && (
          <View style={styles.noRepliesContainer}>
            <Text style={styles.noRepliesText}>まだ返信がありません</Text>
          </View>
        )}
      </ScrollView>

      {/* リポストメニュー */}
      <ActionSheet
        visible={repostMenuVisible}
        onClose={() => setRepostMenuVisible(false)}
        options={repostMenuOptions}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  parentContainer: {
    position: 'relative',
  },
  threadLine: {
    position: 'absolute',
    left: 28,
    top: 60,
    bottom: 0,
    width: 2,
    backgroundColor: colors.border,
  },
  mainPostContainer: {
    backgroundColor: colors.backgroundSecondary,
  },
  repliesContainer: {
    marginTop: spacing.md,
  },
  repliesHeader: {
    ...typography.h4,
    color: colors.text,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.backgroundSecondary,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  noRepliesContainer: {
    padding: spacing.xl,
    alignItems: 'center',
  },
  noRepliesText: {
    ...typography.body,
    color: colors.textSecondary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  errorText: {
    ...typography.body,
    color: colors.error,
    textAlign: 'center',
  },
});
